import { Formik, Form } from 'formik';
import { Laptop } from 'lucide-react';
import { useRouter } from '@uirouter/react';
import { boolean, object, SchemaOf, string } from 'yup';

import {
  validation as tunnelValidation,
  buildDefaultValue as buildTunnelDefaultValue,
} from '@/react/portainer/common/PortainerTunnelAddrField';
import {
  validation as urlValidation,
  buildDefaultValue as buildUrlDefaultValue,
} from '@/react/portainer/common/PortainerUrlField';
import {
  useSettings,
  useUpdateSettingsMutation,
} from '@/react/portainer/settings/queries/useSettings';
import { EnabledWaitingRoomSwitch } from '@/react/portainer/settings/EdgeComputeView/AutomaticEdgeEnvCreation/EnableWaitingRoomSwitch';
import { EdgeComputeConnectivityFields } from '@/react/portainer/settings/EdgeComputeView/EdgeComputeSettings/EdgeComputeConnectivityFields';
import { notifySuccess } from '@/portainer/services/notifications';

import { Switch } from '@@/form-components/SwitchField/Switch';
import { FormControl } from '@@/form-components/FormControl';
import { Widget, WidgetBody, WidgetTitle } from '@@/Widget';
import { LoadingButton } from '@@/buttons/LoadingButton';
import { Button } from '@@/buttons';
import { TextTip } from '@@/Tip/TextTip';

interface FormValues {
  EnableEdgeComputeFeatures: boolean;
  EdgePortainerUrl: string;
  EnableWaitingRoom: boolean;
  Edge: {
    TunnelServerAddress: string;
  };
}

export function InitEdgeView() {
  const router = useRouter();
  const updateSettingsMutation = useUpdateSettingsMutation();
  const settingsQuery = useSettings();

  // Prefill the form from the saved settings when they are set (e.g. from the
  // startup CLI flags), falling back to the browser-derived defaults otherwise.
  const settings = settingsQuery.data;
  const initialValues: FormValues = {
    EnableEdgeComputeFeatures: settings?.EnableEdgeComputeFeatures ?? false,
    EdgePortainerUrl: settings?.EdgePortainerUrl || buildUrlDefaultValue(),
    EnableWaitingRoom: settings ? !settings.TrustOnFirstConnect : true,
    Edge: {
      TunnelServerAddress:
        settings?.Edge.TunnelServerAddress || buildTunnelDefaultValue(),
    },
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center">
      <div className="container">
        <div className="col-md-8 col-md-offset-2 col-sm-10 col-sm-offset-1">
          <Widget>
            <WidgetTitle icon={Laptop} title="Set up Edge Compute" />

            <WidgetBody loading={settingsQuery.isLoading}>
              <p className="text-muted">
                Edge Compute lets Portainer manage environments that it cannot
                reach directly — remote devices, environments behind NAT or a
                firewall, or sites with intermittent connectivity.
              </p>
              <ul className="text-muted ml-4 list-disc">
                <li>
                  Onboard edge agents over a secure reverse tunnel, with no
                  inbound ports to open on the remote side.
                </li>
                <li>
                  Deploy stacks and jobs to many edge environments from a single
                  Portainer instance.
                </li>
                <li>
                  Use async agents for low-bandwidth or intermittently connected
                  sites.
                </li>
              </ul>

              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                validateOnMount
              >
                {({ values, errors, setFieldValue, isValid }) => (
                  <Form className="form-horizontal mt-4" noValidate>
                    <FormControl
                      inputId="edge_enable"
                      label="Enable Edge Compute features"
                      size="small"
                      errors={errors.EnableEdgeComputeFeatures}
                    >
                      <Switch
                        id="edge_enable"
                        data-cy="init-edge-enable-switch"
                        name="edge_enable"
                        className="space-right"
                        checked={values.EnableEdgeComputeFeatures}
                        onChange={(e) =>
                          setFieldValue('EnableEdgeComputeFeatures', e)
                        }
                      />
                    </FormControl>

                    {values.EnableEdgeComputeFeatures && (
                      <>
                        <TextTip color="blue" className="mb-2">
                          These addresses are how edge agents will reach this
                          Portainer instance. They are prefilled from your
                          browser — confirm they are reachable from where your
                          agents run. When edge mTLS is enabled, the URL must
                          use a hostname distinct from the one you use for the
                          web UI. You can change these later in Settings &gt;
                          Edge Compute.
                        </TextTip>

                        <EdgeComputeConnectivityFields
                          dataCyPrefix="init-edge"
                          isMTLSEnabled={false}
                        />

                        <EnabledWaitingRoomSwitch />

                        <TextTip color="blue" className="mb-2">
                          When enabled, new edge agents wait for manual approval
                          in Edge Compute &gt; Waiting Room before they are
                          associated. When disabled, agents are automatically
                          trusted on first connect.
                        </TextTip>
                      </>
                    )}

                    <div className="form-group mt-5">
                      <div className="col-sm-12 flex gap-2">
                        <LoadingButton
                          disabled={!isValid}
                          data-cy="init-edge-submit-button"
                          isLoading={updateSettingsMutation.isLoading}
                          loadingText="Saving..."
                        >
                          {values.EnableEdgeComputeFeatures
                            ? 'Enable and continue'
                            : 'Continue'}
                        </LoadingButton>

                        <Button
                          type="button"
                          color="light"
                          onClick={goToWizard}
                          data-cy="init-edge-skip-button"
                        >
                          Skip
                        </Button>
                      </div>
                    </div>
                  </Form>
                )}
              </Formik>
            </WidgetBody>
          </Widget>
        </div>
      </div>
    </div>
  );

  function goToWizard() {
    router.stateService.go('portainer.wizard');
  }

  function handleSubmit(values: FormValues) {
    if (!values.EnableEdgeComputeFeatures) {
      goToWizard();
      return;
    }

    updateSettingsMutation.mutate(
      {
        EnableEdgeComputeFeatures: true,
        EdgePortainerUrl: values.EdgePortainerUrl,
        TrustOnFirstConnect: !values.EnableWaitingRoom,
        Edge: {
          TunnelServerAddress: values.Edge.TunnelServerAddress,
        },
      },
      {
        onSuccess() {
          notifySuccess('Success', 'Edge Compute enabled');
          goToWizard();
        },
      }
    );
  }
}

function validationSchema(): SchemaOf<FormValues> {
  return object({
    EnableEdgeComputeFeatures: boolean().default(false),
    EnableWaitingRoom: boolean().default(true),
    EdgePortainerUrl: string()
      .default('')
      .when('EnableEdgeComputeFeatures', {
        is: true,
        then: () => urlValidation(),
      }),
    Edge: object({
      TunnelServerAddress: string().default(''),
    }).when('EnableEdgeComputeFeatures', {
      is: true,
      then: (schema) =>
        schema.shape({ TunnelServerAddress: tunnelValidation() }),
    }),
  });
}
